import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogTrigger,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useState } from 'react';
import { cn } from '@/lib/utils';

import {
  RectangleHorizontal,
  RectangleVertical,
  Square,
  Layout,
  Plus,
  Minus,
  GraduationCap,
  Tv,
  UserRound,
  MicVocal,
  SquarePlay
} from 'lucide-react';

import { Youtube, Instagram } from '@/components/Icons';

export interface DeliverableState {
  aspectRatio: string;
  lengthRange: string;
  videoType: string;
  quantity: number;
}

export default function DeliverableDialog({
  onAdd,
}: {
  onAdd: (deliverable: DeliverableState) => void;
}) {
  const [open, setOpen] = useState(false);
  const [deliverable, setDeliverable] = useState<DeliverableState>({
    aspectRatio: '',
    lengthRange: '',
    videoType: '',
    quantity: 1,
  });
  const [step, setStep] = useState(1);
  const [isCustom, setIsCustom] = useState(false);

  function selectAspectRatio(aspectRatio: string) {
    setDeliverable((prev) => ({
      ...prev,
      aspectRatio,
    }));
  }

  function selectLengthRange(lengthRange: string) {
    setDeliverable((prev) => ({
      ...prev,
      lengthRange,
    }));
  }

  function selectVideoType(videoType: string) {
    setIsCustom(false);
    setDeliverable((prev) => ({
      ...prev,
      videoType,
    }));
  }

  function clearVideoType() {
    setDeliverable((prev) => ({
      ...prev,
      videoType: '',
    }));
  }

  function resetDeliverable() {
    setDeliverable({
      aspectRatio: '',
      lengthRange: '',
      videoType: '',
      quantity: 1,
    });
    setIsCustom(false);
    setStep(1);
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger>
        <Button className="cursor-pointer w-35"><SquarePlay />Add Video</Button>
      </DialogTrigger>
      <DialogContent
        className="sm:max-w-3xl"
        onAnimationEnd={() => {
          if (!open) resetDeliverable();
        }}
      >
        <div className="grid grid-cols-5 gap-4 w-full my-4">
          <div
            className={cn('flex flex-col items-center cursor-pointer')}
            onClick={() => step > 1 && setStep(1)}
          >
            <span
              className={cn(
                'bg-muted text-black w-8 h-8 rounded-full flex items-center justify-center text-lg mb-3',
                step === 1 && 'bg-white'
              )}
            >
              1
            </span>
            <span className="text-base mb-1">Style</span>
            <span className="text-xs text-white/60 h-4">
              {deliverable.aspectRatio ? deliverable.aspectRatio : null}
            </span>
          </div>
          <div className={'h-0.5 w-12 self-center mb-6 bg-muted'} />
          <div
            className={cn(
              'flex flex-col items-center',
              step > 2 && 'cursor-pointer'
            )}
            onClick={() => step > 2 && setStep(2)}
          >
            <span
              className={cn(
                'bg-muted text-black w-8 h-8 rounded-full flex items-center justify-center text-lg mb-3',
                step === 2 && 'bg-white'
              )}
            >
              2
            </span>
            <span className="text-base mb-1">Length</span>
            <span className="text-xs text-white/60 h-4">
              {deliverable.lengthRange ? deliverable.lengthRange : null}
            </span>
          </div>
          <div className={'h-0.5 w-12 self-center mb-6 bg-muted'} />
          <div
            className={cn(
              'flex flex-col items-center',
              step > 3 && 'cursor-pointer'
            )}
            onClick={() => step > 3 && setStep(3)}
          >
            <span
              className={cn(
                'bg-muted text-black w-8 h-8 rounded-full flex items-center justify-center text-lg mb-3',
                step === 3 && 'bg-white'
              )}
            >
              3
            </span>
            <span className="text-base mb-1">Type</span>
            <span className="text-xs text-white/60 h-4">
              {deliverable.videoType ? deliverable.videoType : null}
            </span>
          </div>
        </div>

        <DialogHeader>
          <DialogTitle>Add Video</DialogTitle>
          <DialogDescription>
            Select video specifications.
          </DialogDescription>
        </DialogHeader>
        <div className="min-h-85 flex flex-col justify-start overflow-y-auto">
          {step === 1 && (
            <>
              <div className="grid grid-cols-2 gap-4">
                <Button
                  variant={
                    deliverable.aspectRatio === 'Landscape'
                      ? 'default'
                      : 'outline'
                  }
                  className="flex flex-col w-full h-full py-8 cursor-pointer"
                  onClick={() => selectAspectRatio('Landscape')}
                  asChild
                >
                  <div>
                    <RectangleHorizontal className="mb-2 size-10" />
                    <span className="text-lg">Landscape (16:9)</span>
                  </div>
                </Button>
                <Button
                  variant={
                    deliverable.aspectRatio === 'Vertical'
                      ? 'default'
                      : 'outline'
                  }
                  className="flex flex-col w-full h-full py-8 cursor-pointer"
                  onClick={() => selectAspectRatio('Vertical')}
                  asChild
                >
                  <div>
                    <RectangleVertical className="mb-2 size-10" />
                    <span className="text-lg">Vertical (9:16)</span>
                  </div>
                </Button>
                <Button
                  variant={
                    deliverable.aspectRatio === 'Video Post'
                      ? 'default'
                      : 'outline'
                  }
                  className="flex flex-col w-full h-full py-8 cursor-pointer"
                  onClick={() => selectAspectRatio('Video Post')}
                  asChild
                >
                  <div>
                    <Layout className="mb-2 size-10" />
                    <span className="text-lg">Video Post (4:5)</span>
                  </div>
                </Button>
                <Button
                  variant={
                    deliverable.aspectRatio === 'Square' ? 'default' : 'outline'
                  }
                  className="flex flex-col w-full h-full py-8 cursor-pointer"
                  onClick={() => selectAspectRatio('Square')}
                  asChild
                >
                  <div>
                    <Square className="mb-2 size-10" />
                    <span className="text-lg">Square (1:1)</span>
                  </div>
                </Button>
              </div>
            </>
          )}

          {step === 2 && (
            <>
              <div className="grid grid-cols-2 gap-4">
                <Button
                  variant={
                    deliverable.lengthRange === '10 - 60 seconds'
                      ? 'default'
                      : 'outline'
                  }
                  className="cursor-pointer text-lg py-7"
                  onClick={() => selectLengthRange('10 - 60 seconds')}
                >
                  10 - 60 seconds
                </Button>
                <Button
                  variant={
                    deliverable.lengthRange === '1 - 3 minutes'
                      ? 'default'
                      : 'outline'
                  }
                  className="cursor-pointer text-lg py-7"
                  onClick={() => selectLengthRange('1 - 3 minutes')}
                >
                  1 - 3 minutes
                </Button>
                <Button
                  variant={
                    deliverable.lengthRange === '3 - 5 minutes'
                      ? 'default'
                      : 'outline'
                  }
                  className="cursor-pointer text-lg py-7"
                  onClick={() => selectLengthRange('3 - 5 minutes')}
                >
                  3 - 5 minutes
                </Button>
                <Button
                  variant={
                    deliverable.lengthRange === '5 - 10 minutes'
                      ? 'default'
                      : 'outline'
                  }
                  className="cursor-pointer text-lg py-7"
                  onClick={() => selectLengthRange('5 - 10 minutes')}
                >
                  5 - 10 minutes
                </Button>
                <Button
                  variant={
                    deliverable.lengthRange === '10 - 15 minutes'
                      ? 'default'
                      : 'outline'
                  }
                  className="cursor-pointer text-lg py-7"
                  onClick={() => selectLengthRange('10 - 15 minutes')}
                >
                  10 - 15 minutes
                </Button>
                <Button
                  variant={
                    deliverable.lengthRange === '15 - 30 minutes'
                      ? 'default'
                      : 'outline'
                  }
                  className="cursor-pointer text-lg py-7"
                  onClick={() => selectLengthRange('15 - 30 minutes')}
                >
                  15 - 30 minutes
                </Button>
                <Button
                  variant={
                    deliverable.lengthRange === '30+ minutes'
                      ? 'default'
                      : 'outline'
                  }
                  className="col-span-2 cursor-pointer text-lg py-7"
                  onClick={() => selectLengthRange('30+ minutes')}
                >
                  30+ minutes
                </Button>
              </div>
            </>
          )}

          {step === 3 && (
            <>
              <div className="grid grid-cols-3 gap-4">
                <Button
                  variant={
                    !isCustom && deliverable.videoType === 'Long-form'
                      ? 'default'
                      : 'outline'
                  }
                  className="cursor-pointer flex flex-col items-center text-base py-12"
                  onClick={() => {
                    selectVideoType('Long-form');
                  }}
                >
                  <Youtube className="size-6" />
                  <span>Long-form</span>
                </Button>
                <Button
                  variant={
                    !isCustom && deliverable.videoType === 'Short-form'
                      ? 'default'
                      : 'outline'
                  }
                  className="cursor-pointer flex flex-col items-center text-base py-12"
                  onClick={() => {
                    selectVideoType('Short-form');
                  }}
                >
                  <Instagram className="size-5" />
                  <span>Short-form</span>
                </Button>
                <Button
                  variant={
                    !isCustom && deliverable.videoType === 'Educational Lesson'
                      ? 'default'
                      : 'outline'
                  }
                  className="cursor-pointer flex flex-col items-center text-base py-12"
                  onClick={() => {
                    selectVideoType('Educational Lesson');
                  }}
                >
                  <GraduationCap className="size-6" />
                  <span>Educational Lesson</span>
                </Button>
                <Button
                  variant={
                    !isCustom && deliverable.videoType === 'Ad / Promo'
                      ? 'default'
                      : 'outline'
                  }
                  className="cursor-pointer flex flex-col items-center text-base py-12"
                  onClick={() => {
                    selectVideoType('Ad / Promo');
                  }}
                >
                  <Tv className="size-5" />
                  <span>Ad / Promo</span>
                </Button>
                <Button
                  variant={
                    !isCustom && deliverable.videoType === 'Testimonial'
                      ? 'default'
                      : 'outline'
                  }
                  className="cursor-pointer flex flex-col items-center text-base py-12"
                  onClick={() => {
                    selectVideoType('Testimonial');
                  }}
                >
                  <UserRound className="size-6" />
                  <span>Testimonial</span>
                </Button>
                <Button
                  variant={
                    !isCustom && deliverable.videoType === 'Podcast'
                      ? 'default'
                      : 'outline'
                  }
                  className="cursor-pointer flex flex-col items-center text-base py-12"
                  onClick={() => {
                    selectVideoType('Podcast');
                  }}
                >
                  <MicVocal className="size-5" />
                  <span>Podcast</span>
                </Button>

                <Button
                  variant={isCustom ? 'default' : 'outline'}
                  className="cursor-pointer text-base py-6 h-full"
                  onClick={() => {
                    setIsCustom(() => true);
                    clearVideoType();
                  }}
                >
                  Custom
                </Button>
                <div
                  className={cn(
                    'flex items-center col-span-2',
                    !isCustom && 'invisible'
                  )}
                >
                  <Input
                    className="text-2xl h-12 text-center"
                    placeholder="Enter video type"
                    value={deliverable.videoType}
                    onChange={(e) =>
                      setDeliverable({
                        ...deliverable,
                        videoType: e.target.value,
                      })
                    }
                  />
                </div>
              </div>
            </>
          )}
        </div>
        <DialogFooter>
          <div className="w-full relative flex items-end pt-8">
            <Button variant="ghost" onClick={() => setOpen(false)}>
              Cancel
            </Button>

            <div className="absolute left-1/2 -translate-x-1/2 flex flex-col items-center gap-2">
              <span className="text-sm text-white/60 pb-1">Quantity</span>
              <div className="flex flex-row gap-2">
                <Button
                  className="cursor-pointer"
                  variant="outline"
                  size="icon"
                  onClick={() =>
                    setDeliverable((prev) => ({
                      ...prev,
                      quantity: prev.quantity > 1 ? prev.quantity - 1 : 1,
                    }))
                  }
                >
                  <Minus />
                </Button>
                <Input
                  value={deliverable.quantity}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, '').slice(0, 2);
                    setDeliverable({
                      ...deliverable,
                      quantity: value ? parseInt(value) : 1,
                    });
                  }}
                  className="w-11 text-center"
                  type="text"
                />
                <Button
                  className="cursor-pointer"
                  variant="outline"
                  size="icon"
                  onClick={() =>
                    setDeliverable((prev) => ({
                      ...prev,
                      quantity: prev.quantity < 99 ? prev.quantity + 1 : 99,
                    }))
                  }
                >
                  <Plus />
                </Button>
              </div>
            </div>
            <div className="ml-auto flex gap-2">
              <Button
                disabled={step === 1}
                variant="outline"
                onClick={() => setStep(step - 1)}
              >
                Back
              </Button>
              {step < 3 ? (
                <Button
                  onClick={() => setStep((prev) => prev + 1)}
                  className="cursor-pointer"
                  disabled={
                    (step === 1 && !deliverable.aspectRatio) ||
                    (step === 2 && !deliverable.lengthRange)
                  }
                >
                  Next
                </Button>
              ) : (
                <Button
                  className="w-40 cursor-pointer"
                  disabled={
                    !deliverable.aspectRatio ||
                    !deliverable.lengthRange ||
                    !deliverable.videoType
                  }
                  onClick={() => {
                    onAdd(deliverable);
                    setOpen(false);
                  }}
                >
                  Add Video{deliverable.quantity > 1 ? 's' : ''}
                </Button>
              )}
            </div>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
